/**
 * React Query Configuration
 * 
 * Centralized configuration for data fetching, caching, and query management
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Stale times for different data types (in milliseconds)
 * 
 * - Short: Data that changes frequently
 * - Medium: Data that changes occasionally  
 * - Long: Data that rarely changes
 * - Static: Data that never changes during a session
 */
export const STALE_TIMES = {
  /** Site settings - 30 seconds */
  siteSettings: 30_000,
  
  /** Pages content - 5 minutes */
  pages: 5 * 60 * 1000,
  
  /** Events listing - 2 minutes */
  events: 2 * 60 * 1000,
  
  /** News articles - 2 minutes */
  news: 2 * 60 * 1000,
  
  /** Gallery albums - 10 minutes */
  gallery: 10 * 60 * 1000,
  
  /** Projects - 10 minutes */
  projects: 10 * 60 * 1000,
  
  /** Daily facts - 1 hour */
  facts: 60 * 60 * 1000,
  
  /** Sponsors/Partners - 30 minutes */
  sponsors: 30 * 60 * 1000,
  
  /** Homepage tiles - 5 minutes */
  homepageTiles: 5 * 60 * 1000,
  
  /** Impact stats - 5 minutes */
  impactStats: 5 * 60 * 1000,
  
  /** Hero images - 5 minutes */
  heroImages: 5 * 60 * 1000,
  
  /** User/Auth data - 1 minute */
  user: 60_000,
  
  /** Analytics data - 5 minutes */
  analytics: 5 * 60 * 1000,
  
  /** SEO data - 5 minutes */
  seo: 5 * 60 * 1000,
  
  /** Static data - never refetch */
  static: Infinity,
  
  /** Small data - 30 seconds */
  small: 30_000,
  
  /** Medium data - 2 minutes */
  medium: 2 * 60 * 1000,
} as const;

/**
 * Garbage collection times (how long to keep inactive data in cache)
 */
export const GC_TIMES = {
  /** Default - 5 minutes */
  default: 5 * 60 * 1000,
  
  /** Large data - 10 minutes */
  large: 10 * 60 * 1000,
  
  /** Small data - 2 minutes */
  small: 2 * 60 * 1000,
  
  /** Never garbage collect */
  never: Infinity,
} as const;

/**
 * Retry configuration
 */
export const RETRY_CONFIG = {
  /** Default retry count */
  count: 3,
  
  /** Delay between retries (ms) */
  delay: 1000,
  
  /** Whether to retry on network errors */
  networkMode: 'online' as const,
} as const;

/**
 * Query key factory for consistent key generation
 */
export const queryKeys = {
  // Site settings
  siteSettings: ['site-settings'] as const,
  
  // Pages
  pages: {
    all: ['pages'] as const,
    list: () => [...queryKeys.pages.all, 'list'] as const,
    detail: (slug: string) => [...queryKeys.pages.all, 'detail', slug] as const,
    media: (pageId: string) => [...queryKeys.pages.all, 'media', pageId] as const,
  },
  
  // Events
  events: {
    all: ['events'] as const,
    list: () => [...queryKeys.events.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.events.all, 'detail', id] as const,
    featured: () => [...queryKeys.events.all, 'featured'] as const,
  },
  
  // News
  news: {
    all: ['news'] as const,
    list: () => [...queryKeys.news.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.news.all, 'detail', id] as const,
  },
  
  // Gallery
  gallery: {
    all: ['gallery'] as const,
    albums: (options?: { isAdmin?: boolean }) => [...queryKeys.gallery.all, 'albums', options] as const,
    album: (id: number) => [...queryKeys.gallery.all, 'album', id] as const,
    photos: (albumId: number) => [...queryKeys.gallery.all, 'photos', albumId] as const,
  },

  // Projects
  projects: {
    all: ['projects'] as const,
    list: (options?: { category?: string; isAdmin?: boolean }) => [...queryKeys.projects.all, 'list', options] as const,
    detail: (id: number) => [...queryKeys.projects.all, 'detail', id] as const,
  },
  
  // Hero Images
  heroImages: {
    all: ['hero-images'] as const,
    list: () => [...queryKeys.heroImages.all, 'list'] as const,
    active: () => [...queryKeys.heroImages.all, 'active'] as const,
    detail: (id: number) => [...queryKeys.heroImages.all, 'detail', id] as const,
  },
  
  // Facts
  facts: {
    all: ['facts'] as const,
    list: () => [...queryKeys.facts.all, 'list'] as const,
    daily: () => [...queryKeys.facts.all, 'daily'] as const,
    detail: (id: string) => [...queryKeys.facts.all, 'detail', id] as const,
  },
  
  // Sponsors/Partners
  sponsors: {
    all: ['sponsors'] as const,
    list: () => [...queryKeys.sponsors.all, 'list'] as const,
    partners: () => [...queryKeys.sponsors.all, 'partners'] as const,
  },
  
  // Homepage
  homepage: {
    tiles: ['homepage', 'tiles'] as const,
    stats: ['homepage', 'stats'] as const,
  },
  
  // Testimonials
  testimonials: {
    all: ['testimonials'] as const,
    list: () => [...queryKeys.testimonials.all, 'list'] as const,
  },
  
  // User/Auth
  user: {
    current: ['user', 'current'] as const,
    admin: ['user', 'admin'] as const,
  },
  
  // Search
  search: ['search'] as const,

  // Analytics
  analytics: {
    all: ['analytics'] as const,
    config: () => [...queryKeys.analytics.all, 'config'] as const,
    metrics: (dateRange?: string) => [...queryKeys.analytics.all, 'metrics', dateRange] as const,
  },

  // SEO
  seo: {
    all: ['seo'] as const,
    data: (identifier: string | number) => [...queryKeys.seo.all, 'data', identifier] as const,
    list: () => [...queryKeys.seo.all, 'list'] as const,
  },
} as const;

/**
 * Create a configured QueryClient instance
 */
export const createQueryClient = () => new QueryClient({
  defaultOptions: {
    queries: {
      // Default stale time
      staleTime: STALE_TIMES.pages,
      
      // Default garbage collection time
      gcTime: GC_TIMES.default,
      
      // Retry configuration
      retry: RETRY_CONFIG.count,
      retryDelay: (attemptIndex) => Math.min(
        RETRY_CONFIG.delay * 2 ** attemptIndex,
        30000
      ),
      
      // Refetch behavior
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      
      // Network mode
      networkMode: RETRY_CONFIG.networkMode,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      
      // Network mode
      networkMode: RETRY_CONFIG.networkMode,
    },
  },
});

/**
 * Default query client instance
 */
export const queryClient = createQueryClient();

/**
 * Invalidate queries by key pattern
 */
export const invalidateQueries = async (keyPattern: readonly unknown[]) => {
  await queryClient.invalidateQueries({ queryKey: keyPattern });
};

/**
 * Prefetch a query
 */
export const prefetchQuery = async <T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  staleTime?: number
) => {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: staleTime ?? STALE_TIMES.pages,
  });
};

export default queryClient;




